import { ShaderManager } from "../ShaderManager";
import { TextureManager } from "../TextureManager";
import { RenderObject, RenderObjectConstructorArgs } from "./RenderObject";
import { Material, ObjectType } from "./types";

export type CubeInitArgs = {
  material?: Material;
} & RenderObjectConstructorArgs;

export class Cube extends RenderObject {
  constructor(args: CubeInitArgs) {
    super(args);
    this.type = ObjectType.CUBE;

    if (args.material) {
      this.material = args.material;
    }

    this.model.vertices = [
      0.0, 0.0, 0.0, 0.0, 0.5, 0.0, 0.5, 0.5, 0.0, 0.5, 0.0, 0.0,

      0.0, 0.0, 0.5, 0.0, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.0, 0.5,

      0.0, 0.5, 0.5, 0.0, 0.5, 0.0, 0.5, 0.5, 0.0, 0.5, 0.5, 0.5,

      0.0, 0.0, 0.5, 0.5, 0.0, 0.5, 0.5, 0.0, 0.0, 0.0, 0.0, 0.0,

      0.5, 0.0, 0.5, 0.5, 0.0, 0.0, 0.5, 0.5, 0.5, 0.5, 0.5, 0.0,

      0.0, 0.0, 0.5, 0.0, 0.0, 0.0, 0.0, 0.5, 0.5, 0.0, 0.5, 0.0,
    ];

    this.model.triangles = [
      //front face
      2, 0, 1, 3, 0, 2,
      //backface
      5, 4, 6, 6, 4, 7,
      //top face
      10, 9, 8, 10, 8, 11,
      //bottom face
      13, 12, 14, 14, 12, 15,
      //right face
      18, 16, 17, 18, 17, 19,
      //left face
      22, 21, 20, 23, 21, 22,
    ];

    // UVs (full texture per face)
    this.model.uvs = [
      0, 0, 1, 0, 1, 1, 0, 1, // front
      0, 0, 1, 0, 1, 1, 0, 1, // back
      0, 0, 1, 0, 1, 1, 0, 1, // top
      0, 0, 1, 0, 1, 1, 0, 1, // bottom
      0, 0, 1, 0, 1, 1, 0, 1, // right
      0, 0, 1, 0, 1, 1, 0, 1, // left
    ];

    // Normals per face
    this.model.normals = [
      0.0, 0.0, -1.0, 0.0, 0.0, -1.0, 0.0, 0.0, -1.0, 0.0, 0.0, -1.0,

      0.0, 0.0, 1.0, 0.0, 0.0, 1.0, 0.0, 0.0, 1.0, 0.0, 0.0, 1.0,

      0.0, 1.0, 0.0, 0.0, 1.0, 0.0, 0.0, 1.0, 0.0, 0.0, 1.0, 0.0,

      0.0, -1.0, 0.0, 0.0, -1.0, 0.0, 0.0, -1.0, 0.0, 0.0, -1.0, 0.0,

      1.0, 0.0, 0.0, 1.0, 0.0, 0.0, 1.0, 0.0, 0.0, 1.0, 0.0, 0.0,

      -1.0, 0.0, 0.0, -1.0, 0.0, 0.0, -1.0, 0.0, 0.0, -1.0, 0.0, 0.0,
    ];
  }

  setup(gl: WebGL2RenderingContext, shaderManager: ShaderManager, textureManager: TextureManager) {
    // get the textures and put them in the material
    if (this.material) {
      this.material.diffuseTexture = textureManager.getTexture(this.material.diffuseTexture?.name);
      this.material.normalTexture = textureManager.getTexture(this.material.normalTexture?.name, "normal");
    }

    const tangentData = this.calculateBitangents();
    this.model.bitangents = tangentData.bitangents.flat();
    this.model.tangents = tangentData.tangents.flat();
    this.calculateCentroid(this.model.vertices);
    this.initShader(gl, shaderManager);
    this.initBuffers(gl);
  }

  clone(name: string): Cube {
    return new Cube({
      name,
      position: this.model.position,
      scale: this.model.scale,
      rotation: this.model.rotation,
      diffuseTextureFile: this.material?.diffuseTexture?.name,
      normalTextureFile: this.material?.normalTexture?.name,
      material: this.material,
    });
  }
}
